import {
  useQueryStates,
  parseAsArrayOf,
  parseAsString,
  parseAsInteger,
  parseAsJson,
} from 'nuqs';
import { useCallback, useMemo } from 'react';
import { STEPS } from '../constants';

/**
 * Custom parser that sanitizes string values from URL
 * Converts string "undefined" or "null" to actual null
 * Prevents malicious users from bypassing validation
 */
const parseAsNullableString = {
  parse: (value) => {
    if (!value || value === 'undefined' || value === 'null') {
      return null;
    }
    return value;
  },
  serialize: (value) => {
    return value || '';
  },
};

/**
 * URL parameter parsers for risk calculator state
 */
const urlParsers = {
  step: parseAsInteger.withDefault(STEPS.CULTURES),
  cultures: parseAsArrayOf(parseAsString).withDefault([]),
  treatmentType: parseAsNullableString,
  applicationTechnique: parseAsNullableString,
  products: parseAsJson((value) => {
    if (Array.isArray(value)) return value;
    return null;
  }).withDefault([]),
  view: parseAsString.withDefault('form'),
};

/**
 * Hook for managing URL state with nuqs
 * Pure URL state management without business logic
 *
 * @returns {Object} URL state and setters
 */
export function useURLState() {
  // Use nuqs for URL state management
  const [urlState, setUrlState] = useQueryStates(urlParsers, {
    history: 'replace',
    shallow: false,
    throttleMs: 300,
    clearOnDefault: true,
  });

  // Navigation actions
  const goToStep = useCallback(
    (step) => {
      if (step >= STEPS.CULTURES && step <= STEPS.COMMERCIAL_PRODUCTS) {
        setUrlState({ step });
      }
    },
    [setUrlState]
  );

  const goToNextStep = useCallback(() => {
    if (urlState.step < STEPS.COMMERCIAL_PRODUCTS) {
      setUrlState({ step: urlState.step + 1 });
    }
  }, [urlState.step, setUrlState]);

  const goToPreviousStep = useCallback(() => {
    if (urlState.step > STEPS.CULTURES) {
      setUrlState({ step: urlState.step - 1 });
    }
  }, [urlState.step, setUrlState]);

  // View mode actions
  const setView = useCallback(
    (view) => {
      setUrlState({ view });
    },
    [setUrlState]
  );

  const goToResults = useCallback(() => {
    setUrlState({ view: 'results' });
  }, [setUrlState]);

  const goToForm = useCallback(() => {
    setUrlState({ view: 'form' });
  }, [setUrlState]);

  // Build selections object for compatibility
  const selections = useMemo(
    () => ({
      cultures: urlState.cultures || [],
      treatmentType: urlState.treatmentType || null,
      applicationTechnique: urlState.applicationTechnique || null,
      commercialProducts: urlState.products || [],
    }),
    [urlState]
  );

  // Reset all URL state
  const resetUrlState = useCallback(() => {
    setUrlState({
      step: STEPS.CULTURES,
      cultures: [],
      treatmentType: null,
      applicationTechnique: null,
      products: [],
      view: 'form',
    });
  }, [setUrlState]);

  // Helper flags
  const isFirstStep = urlState.step === STEPS.CULTURES;
  const isLastStep = urlState.step === STEPS.COMMERCIAL_PRODUCTS;

  // Get shareable URL
  const getShareableURL = useCallback(() => {
    return window.location.href;
  }, []);

  return {
    // Raw URL state
    urlState,
    setUrlState,

    // Derived state
    currentStep: urlState.step,
    currentView: urlState.view,
    selections,

    // Navigation
    goToStep,
    goToNextStep,
    goToPreviousStep,

    // View
    setView,
    goToResults,
    goToForm,

    // Reset
    resetUrlState,

    // Flags
    isFirstStep,
    isLastStep,

    // Sharing
    getShareableURL,
  };
}

export default useURLState;
